import React from 'react';
import { DownloadIcon, ExternalLinkIcon, RotateCcwIcon, PlusIcon, RefreshCwIcon, CheckCircle2Icon, AlertTriangleIcon } from 'lucide-react';
import type { DestinationId, DestinationRunStatus } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { StatusBadge } from '../ui/StatusBadge';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  results: Record<DestinationId, DestinationRunStatus>;
  totalRecords: number;
  onStartAnother: () => void;
  onRetry: (id: DestinationId) => void;
}
export function ResultsView({
  selected,
  results,
  totalRecords,
  onStartAnother,
  onRetry
}: Props) {
  const failed = 14;
  const skipped = 8;
  const duplicates = 5;
  const created = totalRecords - failed - skipped;
  const anyFailed = selected.some(id => results[id] === 'failed');
  const allGood = selected.every(id => results[id] === 'completed');
  const stats = [{
    label: 'Records submitted',
    value: totalRecords
  }, {
    label: 'Salesforce created',
    value: results.salesforce === 'failed' ? 0 : created
  }, {
    label: 'Bonzo prospects',
    value: results.bonzo === 'failed' ? 0 : created
  }, {
    label: 'Five9 uploaded',
    value: results.five9 === 'failed' ? 0 : created
  }, {
    label: 'Skipped',
    value: skipped
  }, {
    label: 'Failed',
    value: failed
  }, {
    label: 'Duplicates',
    value: duplicates
  }, {
    label: 'Duration',
    value: '2m 14s',
    raw: true
  }];
  return <div className="space-y-6" data-id="element-377" data-mp="1a67qy7iciehy">
      {/* Headline */}
      <div className={cn('flex items-center gap-4 rounded-2xl border p-5', allGood ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50')} data-id="element-378" data-mp="1a7iwabrfvub6">
        <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full', allGood ? 'bg-emerald-100' : 'bg-amber-100')} data-id="element-379" data-mp="1p1xiel6avju2">
          {allGood ? <CheckCircle2Icon className="h-6 w-6 text-emerald-600" data-id="element-380" data-mp="ugjhle7pvjat" /> : <AlertTriangleIcon className="h-6 w-6 text-amber-600" data-id="element-381" data-mp="194cuus9by9q8" />}
        </div>
        <div data-id="element-382" data-mp="14cuzcjq2t1cn">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-383" data-mp="14cd1xjrogh35">
            {allGood ? 'Upload completed successfully' : 'Upload finished with some issues'}
          </h3>
          <p className="text-sm text-gray-600" data-id="element-384" data-mp="1d4ohubp60m4v">
            {anyFailed ? 'Some destinations completed while others failed. Each result is shown independently below.' : 'All records were processed. Review per-destination results below.'}
          </p>
        </div>
      </div>

      {/* Per-destination results (independent) */}
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-385" data-mp="3ov8wldyns6ja">
        {selected.map(id => {
        const def = DESTINATIONS.find(d => d.id === id)!;
        const st = results[id];
        const isFailed = st === 'failed';
        return <div key={id} className={cn('rounded-2xl border bg-white p-5 shadow-card', isFailed ? 'border-red-200' : 'border-gray-200')} data-id="element-386" data-mp="29b9o1yba8v0v">
              <div className="flex items-center justify-between" data-id="element-387" data-mp="1zkng8q9t58sq">
                <div className="flex items-center gap-3" data-id="element-388" data-mp="1llnnp7j6c9ra">
                  <img src={def.icon} alt="" className="h-10 w-10 rounded-lg" data-id="element-389" data-mp="1poc17siu24ul" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-390" data-mp="1pm7msgnb5ng2">
                    {def.name}
                  </p>
                </div>
                <StatusBadge status={st} size="sm" data-id="element-391" data-mp="1gmua7f3w0ir4" />
              </div>
              <div className="mt-4 space-y-1.5 text-sm" data-id="element-392" data-mp="362ka4kaokfnr">
                <Row label="Created" value={isFailed ? '0' : formatNumber(created)} data-id="element-393" data-mp="3633hx86m9no2" />
                <Row label="Skipped" value={isFailed ? '—' : formatNumber(skipped)} data-id="element-394" data-mp="1azf7dhr400si" />
                <Row label="Failed" value={isFailed ? formatNumber(totalRecords) : formatNumber(failed)} danger={isFailed} data-id="element-395" data-mp="161dph05f67im" />
              </div>
              {isFailed && <button type="button" onClick={() => onRetry(id)} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100" data-id="element-396" data-mp="s12zs0fuiyz1">
                  <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-397" data-mp="eapq9tnb3c7l" />
                  Retry {def.name} upload
                </button>}
            </div>;
      })}
      </div>

      {/* Overall stats */}
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-card" data-id="element-398" data-mp="4bqngrp82asi">
        <h3 className="mb-4 text-sm font-semibold text-gray-900" data-id="element-399" data-mp="2fxsotha2rmu5">
          Upload report
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-400" data-mp="2kvkd81bigqtl">
          {stats.map(s => <div key={s.label} className="rounded-xl bg-gray-50 p-3" data-id="element-401" data-mp="2px9p1j6k58iq">
              <p className="text-xl font-bold text-gray-900" data-id="element-402" data-mp="2tx20c3nc5srs">
                {s.raw ? s.value : formatNumber(Number(s.value))}
              </p>
              <p className="text-xs font-medium text-gray-500" data-id="element-403" data-mp="1x5zrxbrvxhh5">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-404" data-mp="267g1jhavlfkb">
        <div className="flex flex-wrap items-center gap-2" data-id="element-405" data-mp="2azr3epp6fbfi">
          <ActionBtn icon={DownloadIcon} label="Download full report" data-id="element-406" data-mp="2b1bz4in890ey" />
          <ActionBtn icon={DownloadIcon} label="Download failed records" data-id="element-407" data-mp="1egjn4c9632ux" />
          <ActionBtn icon={ExternalLinkIcon} label="View Salesforce leads" data-id="element-408" data-mp="1ii4wvoqen10w" />
          {anyFailed && <button type="button" onClick={() => selected.filter(id => results[id] === 'failed').forEach(onRetry)} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100" data-id="element-409" data-mp="2lssjc3mwt1sj">
              <RotateCcwIcon className="h-4 w-4" data-id="element-410" data-mp="22vhxbkqag4v1" />
              Retry failed destinations
            </button>}
        </div>
        <button type="button" onClick={onStartAnother} className="inline-flex items-center gap-2 rounded-lg bg-salesforce-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-salesforce-600" data-id="element-411" data-mp="1xy803iwnqown">
          <PlusIcon className="h-4 w-4" data-id="element-412" data-mp="3n98fgb0lh2b7" />
          Start another upload
        </button>
      </div>
    </div>;
}
function Row({
  label,
  value,
  danger
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return <div className="flex justify-between" data-id="element-413" data-mp="3qv3jwosgdfe2">
      <span className="text-gray-500" data-id="element-414" data-mp="3vs3muqu0a20y">{label}</span>
      <span className={cn('font-semibold', danger ? 'text-red-600' : 'text-gray-900')} data-id="element-415" data-mp="3vph743wyog7n">
        {value}
      </span>
    </div>;
}
function ActionBtn({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" data-id="element-416" data-mp="dematju8vf0o">
      <Icon className="h-4 w-4" data-id="element-417" data-mp="3lyj33i4g18t" />
      {label}
    </button>;
}